import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { JWT } from 'constants/system';
import { USER_TYPES } from 'constants/userTypes';
import { ensureArray } from 'utils/arrays';
import compareUserMode from 'utils/compareUserMode';
import { getCookie } from 'utils/cookies';
import { ensureObject } from 'utils/objects';

/**
 * Provide user redux state
 */
const useUserRedux = () => {
  const userData = useSelector(state => state.user);
  const {
    type,
    isDemoDistrict,
    userCapabilities,
    moduleAccess,
    studentPreviewCounselor,
    isAlumni,
    currentUserMode,
  } = userData;
  const hasCapability = useCallback(
    capabilityName =>
      ensureArray(userCapabilities).filter(e => e.name === capabilityName)
        ?.length > 0,
    [userCapabilities],
  );
  const isDemoStudent =
    type.includes('student') &&
    Object.keys(ensureObject(studentPreviewCounselor)).length > 0;

  const isOAuth2Authenticated = type === 'guest' && getCookie(JWT);

  const hasModuleAccess = moduleName => moduleAccess?.[moduleName] || false;

  const isK12Admin = compareUserMode(currentUserMode, 'k12_admin', type);
  return {
    data: userData,
    isStudent: type.includes('student'),
    isGuardian: compareUserMode(currentUserMode, 'guardian', type),
    isK12Admin,
    // using isCounselor is deprecated now, we should use isK12Admin so it'll be easier to remove that boolean from codebase.
    isCounselor: isK12Admin,
    isMember: compareUserMode(currentUserMode, 'member', type),
    isLorWriter: type.includes('recommender'),
    isSupervisor: type.includes('supervisor'),
    isOrganizationGuest: type.includes(USER_TYPES.ORGANIZATION_GUEST),
    isCounselorAndGuardian:
      type.includes('guardian') && type.includes('k12_admin'),
    isOAuth2Authenticated,
    isAlumni,
    isDemoDistrict,
    isDemoStudent,
    isSuperUser: userData.isSuperUser,
    isLoggedIn: type !== 'guest',
    moduleAccess,
    action: {
      hasCapability,
      hasModuleAccess,
    },
    isDemoAccount: isDemoDistrict || isDemoStudent,
  };
};

export default useUserRedux;
